<script lang="ts">
  import { clearLayers, layers } from '../state/layers.svelte'
  import { layout } from '../state/layout.svelte'
  import Icon from './Icon.svelte'
  import LayerControl from './LayerControl.svelte'

  /**
   * The layer stack over the map, on both layouts. A phone's Map section
   * raises it as a square sheet from the bottom, above the nav (Phase 11 plan
   * §5.2), and its button is a 48 px square at the top right, inverted while
   * the sheet is open. The desktop hangs it as a panel to the left of the
   * map's controls column (§7), and that column draws the button itself
   * (`fab={false}`), between zoom and My location.
   *
   * Deliberately not a panel in the sidebar or a section. Either way that
   * means scrolling away from the map to change a layer and scrolling back to
   * see what changed.
   */

  interface Props {
    /** Open; bound by both layouts, whose layer chip opens it too. */
    open?: boolean
    /** Draw the phone's own button. The desktop's is in its controls column. */
    fab?: boolean
  }

  let { open = $bindable(false), fab = true }: Props = $props()

  // Escape closes a dialog; on desktop the scrim is invisible, so without this
  // the only way out is finding the button again.
  function onKeydown(e: KeyboardEvent) {
    if (e.key === 'Escape') open = false
  }
</script>

<svelte:window onkeydown={open ? onKeydown : undefined} />

{#if fab}
  <button
    type="button"
    class="layers-square touch-target"
    aria-label="Map layers"
    title="Layers"
    aria-expanded={open}
    onclick={() => (open = !open)}
  >
    <Icon name="layers" size={22} />
  </button>
{/if}

{#if open}
  <!-- Transparent: it is there to catch the tap on the map that dismisses
       the panel, not to dim a map the user is still reading. -->
  <!-- svelte-ignore a11y_click_events_have_key_events, a11y_no_static_element_interactions -->
  <div class="scrim" onclick={() => (open = false)}></div>
  <div
    class="layer-sheet"
    class:desktop={layout.isDesktop}
    role="dialog"
    aria-label="Map layers"
  >
    <!-- Round 2 (Stage 10.5, 2a): the title and the count, then the way out.
         The desktop also offers "Turn all off"; on the phone the sheet is
         one tap from the map and the rows are the control. -->
    <header>
      <h2>Layers</h2>
      {#if layout.isDesktop}
        <span class="on-count">{layers.active.length} on</span>
      {:else if layers.active.length > 0}
        <span class="count">{layers.active.length} on</span>
      {/if}
      <span class="spacer"></span>
      {#if layout.isDesktop && layers.active.length > 0}
        <button type="button" class="all-off" onclick={clearLayers}>Turn all off</button>
      {/if}
      <button type="button" class="close touch-target" aria-label="Close" onclick={() => (open = false)}>
        <Icon name="x" size={layout.isDesktop ? 18 : 22} strokeWidth={2.2} />
      </button>
    </header>
    <div class="scroll">
      <LayerControl desktop={layout.isDesktop} />
    </div>
  </div>
{/if}

<style>
  /* Text on a fill, so the field pair rather than 1B's bare accent, which is
     3.76:1 under 11 px type on the light theme (§7.1). */
  .count {
    background: var(--color-field);
    color: var(--color-on-field);
    font-size: 11px;
    font-weight: 800;
    font-variant-numeric: tabular-nums;
    padding: 1px 6px;
  }

  /* The phone's Map section: a 48 px square of ground on the float shadow,
     top right; inverted ink while its sheet is open, never a hue. */
  .layers-square {
    position: absolute;
    top: calc(var(--safe-top) + 12px);
    right: 12px;
    z-index: 18;
    width: 48px;
    height: 48px;
    display: grid;
    place-items: center;
    padding: 0;
    border: 0;
    background: var(--color-bg);
    box-shadow: var(--float-shadow);
    color: var(--color-text);
    cursor: pointer;
  }

  .layers-square[aria-expanded='true'] {
    background: var(--color-text);
    color: var(--color-bg);
  }

  .scrim {
    position: absolute;
    inset: 0;
    z-index: 40;
  }

  /* The phone: a square sheet from the bottom of the map, which ends at the
     nav, under a 4 px ink rule, with a drag bar drawn as a mark. It is not
     draggable: the Layers button, a tap on the map or Escape closes it. */
  .layer-sheet {
    position: absolute;
    z-index: 41;
    left: 0;
    right: 0;
    bottom: 0;
    max-height: 74%;
    display: flex;
    flex-direction: column;
    background: var(--color-bg);
    border-top: 4px solid var(--color-text);
    box-shadow: var(--panel-shadow);
  }

  .layer-sheet:not(.desktop)::before {
    content: '';
    flex: none;
    display: block;
    width: 40px;
    height: 4px;
    margin: 8px auto 0;
    background: var(--color-rule);
  }

  /* The desktop: a panel hanging to the left of the controls column (Phase
     11 plan §7), one column at the mockup's width so it covers as little of
     the map as it can, clear of the timeline card along the bottom. */
  .layer-sheet.desktop {
    top: 12px;
    right: 72px;
    left: auto;
    bottom: auto;
    width: min(360px, calc(100% - 96px));
    max-height: calc(100% - 180px);
    box-shadow: var(--panel-shadow);
  }

  header {
    flex: none;
    display: flex;
    align-items: center;
    gap: 10px;
    height: 56px;
    padding: 0 6px 0 20px;
    border-bottom: 2px solid var(--color-divider);
  }

  .desktop header {
    height: 48px;
    padding: 0 8px 0 16px;
  }

  h2 {
    margin: 0;
    font-size: 20px;
    font-weight: 800;
  }

  .desktop h2 {
    font-size: 16px;
  }

  .on-count {
    font-size: 12px;
    color: var(--color-muted);
  }

  header .count {
    font-size: 12px;
  }

  .spacer {
    flex: 1;
  }

  .all-off {
    height: 32px;
    padding: 0 10px;
    border: 0;
    background: none;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
  }

  .all-off:hover,
  .close:hover {
    background: color-mix(in srgb, var(--color-text) 7%, transparent);
  }

  .close {
    width: 44px;
    height: 44px;
    display: grid;
    place-items: center;
    border: 0;
    background: none;
    cursor: pointer;
  }

  .desktop .close {
    width: 32px;
    height: 32px;
  }

  .scroll {
    flex: 1;
    min-height: 0;
    overflow-y: auto;
    overscroll-behavior: contain;
  }
</style>
